import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { t } from 'translation'
import LoadingButton from 'components/ui/LoadingButton/LoadingButton'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { connect } from 'react-redux'

import AuthErrorBox from 'components/Auth/AuthErrorBox'
import CheckboxItem from 'components/CheckboxItem/CheckboxItem'

import { accountsApi } from 'api'
import { authFail, resetAuthError, setOwnInitialPassword } from 'store/actions'

import history from 'utils/appHistory'

const FORM_INPUTS = [
  {
    id: 'user_prescriber_code',
    name: 'prescriberCode',
    type: 'text',
    labelText: 'PRESCRIBER_CODE',
    fieldClass: 'read_only',
  },
  {
    id: 'user_email_address',
    name: 'email',
    type: 'email',
    labelText: 'EMAIL',
    fieldClass: 'read_only',
  },
  {
    id: 'user_password',
    name: 'password',
    type: 'password',
    labelText: 'PASSWORD',
    fieldClass: '',
  },
  {
    id: 'confirm_user_password',
    name: 'passwordConfirm',
    type: 'password',
    labelText: 'PASSWORD_CONFIRM',
    fieldClass: '',
  },
]

const signupSchema = Yup.object().shape({
  email: Yup.string().email('INVALID_EMAIL').required('EMAIL_IS_REQUIRED'),
  prescriberCode: Yup.string().matches(
    /^[A-Z0-9]{6,10}$/,
    'PRESCRIBER_CODE_MUST_CONTAIN'
  ),
  password: Yup.string()
    .required('PASSWORD_IS_REQUIRED')
    .min(6, 'PASSWORD_IS_TOO_SHORT')
    .max(256, 'PASSWORD_IS_TOO_LONG')
    .matches(
      /^.*(?=.{6,})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
      'PASSWORD_MUST_CONTAIN'
    ),
  passwordConfirm: Yup.string()
    .required('PASSWORD_IS_REQUIRED')
    .oneOf([Yup.ref('password')], 'PASSWORDS_DO_NOT_MATCH'),
})

const SignupStepTwo = ({
  alwaysOnWelcomeVideo,
  authFail,
  authError,
  setSignupStep,
  email,
  inviteId,
  price,
  password,
  setPassword,
  passwordConfirm,
  setPasswordConfirm,
  prescriberCode,
  resetAuthError,
  setOwnInitialPassword,
}) => {
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const prescriberCodeHandler = (value, setFieldValue) =>
    /^[A-Z0-9]{0,10}$/i.test(value) &&
    setFieldValue('prescriberCode', value.toUpperCase())

  return (
    <Formik
      initialValues={{
        email,
        prescriberCode,
        password,
        passwordConfirm,
        termsA: false,
        termsB: false,
      }}
      validationSchema={signupSchema}
      onSubmit={async ({ password, passwordConfirm, termsA, termsB }) => {
        resetAuthError()
        if (!termsA || !termsB) return authFail('LEGAL_TERMS_UNCHECKED')

        setPassword(password)
        setPasswordConfirm(passwordConfirm)

        //if (Number(price) > 0) return setSignupStep('three')

        setIsLoading(true)

        try {
          const { result, error } = await accountsApi.createPatientAccount({
            email,
            inviteId,
          })

          if (error) throw Error(error?.message)

          const { code } = result

          const passwordSetSuccess = await setOwnInitialPassword({
            email,
            tempPassword: code,
            ownPassword: password,
          })

          setSuccess(passwordSetSuccess)
          setIsLoading(false)
          if (!passwordSetSuccess) throw Error('ERROR')

          if (!window.cordova) return setSignupStep('four')
          const redirectPath = alwaysOnWelcomeVideo ? `/welcome` : '/indication-select'
          return history.replace(redirectPath)
        } catch (error) {
          console.log(
            'Error creating account: ',
            error?.error_description || error?.message || error
          )
          setIsLoading(false)
          return authFail(error)
        }
      }}
    >
      {({
        errors,
        touched,
        setFieldValue,
        handleChange,
        submitForm,
        status,
        setStatus,
      }) => (
        <Form className="auth_block">
          <h1 className="section_title v4_mod color_mod center_mod offset_v4_mod">
            {t('signup.SIGNING_UP')}
          </h1>
          <div className="auth_block_descr offset_mod center_lvl_mod">
            {t('signup.SIGN_UP_DESCR')}
          </div>
          {!success && (authError || status === 'clicked') ? (
            <AuthErrorBox
              formFields={['password', 'passwordConfirm']}
              formTouched={touched}
              formErrors={errors}
              apiResponseError={authError || ''}
              resetApiResponseError={resetAuthError}
            />
          ) : null}
          <div className="auth_block_in">
            <div className="auth_block_input_w_list offset_v1_mod">
              <ul className="auth_block_input_list">
                {FORM_INPUTS.map(({ id, name, type, labelText, fieldClass }, index) =>
                  name === 'prescriberCode' && !prescriberCode ? null : (
                    <li
                      className={`auth_block_input_list_item ${
                        index === 0 ? 'v1_mod' : ''
                      }`}
                      key={id}
                    >
                      <dl className="form_cell form_cell_v1_mod">
                        <dt className="form_cell_title form_v3_mod">
                          <label htmlFor={id}>{t(`signup.${labelText}`)}</label>
                        </dt>
                        <dd className="form_field_wrap form_v3_mod">
                          <Field
                            className={`form_field v4_mod ${
                              status === 'clicked' && errors[name] ? 'error' : ''
                            } ${fieldClass}`}
                            type={type}
                            name={name}
                            id={id}
                            disabled={isLoading}
                            onChange={
                              id === 'user_prescriber_code'
                                ? (event) =>
                                    prescriberCodeHandler(
                                      event.target.value,
                                      setFieldValue
                                    )
                                : handleChange
                            }
                            readOnly={name === 'prescriberCode' || name === 'email'}
                          />
                        </dd>
                      </dl>
                    </li>
                  )
                )}
                <li className="form_controller login_mod">
                  <CheckboxItem
                    name="termsA"
                    id="login_checkbox"
                    label={
                      <>
                        {t(`signup.LEGAL_CHECKBOX_1_LABEL_A`)}
                        <Link
                          to={{ pathname: t('links.TERMS_PATIENT') }}
                          target="_blank"
                          rel="noopener"
                        >
                          {t(`signup.LEGAL_CHECKBOX_1_LABEL_LINK`)}
                        </Link>
                        {t(`signup.LEGAL_CHECKBOX_1_LABEL_B`)}
                      </>
                    }
                    disabled={isLoading}
                  />
                </li>
                <li className="form_controller login_mod">
                  <CheckboxItem
                    name="termsB"
                    id="login_checkbox_2"
                    label={
                      <>
                        {t(`signup.LEGAL_CHECKBOX_2_LABEL_A`)}
                        <Link
                          to={{ pathname: t('links.PRIVACY') }}
                          target="_blank"
                          rel="noopener"
                        >
                          {t(`signup.LEGAL_CHECKBOX_2_LABEL_LINK`)}
                        </Link>
                        {t(`signup.LEGAL_CHECKBOX_2_LABEL_B`)}
                      </>
                    }
                    disabled={isLoading}
                  />
                </li>
                <li className="form_controller login_mod">
                  <p className="checkbox_description">
                    {t(`signup.LEGAL_CHECKBOX_LABEL_DESCR`)}
                  </p>
                </li>
              </ul>
            </div>
            <div className="btn_wrap center_lvl_mod">
              <LoadingButton
                className="btn_base_v1 offset_mod"
                isLoading={isLoading}
                disabled={success}
                onClick={(e) => {
                  e.preventDefault()

                  setStatus('clicked')
                  submitForm()
                }}
              >
                {t('signup.CONTINUE')}
              </LoadingButton>
              <span className="auth_block_member_link">
                {t('signup.ALREADY_MEMBER')}
                <Link to="/login">{t('signup.CONNECT')}</Link>
              </span>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}

const mapStateToProps = ({ auth: { error, attributes } }) => ({
  alwaysOnWelcomeVideo: attributes?.permissions?.features?.alwaysOnWelcomeVideo,
  authError: error,
})

const willMapDispatchToProps = {
  authFail,
  resetAuthError,
  setOwnInitialPassword,
}

export default connect(mapStateToProps, willMapDispatchToProps)(SignupStepTwo)
